import "es6-shim";
import "./AdminSetup.css";
import "../../styles/Buttons.css";
import "../../styles/RouteHeader.css";
import * as React from "react";
import { useEffect } from "react";
import { IAdminSetupProps, SettingOption, FlowOption } from "./Types";
import { LanguageCodes } from "../../constants/languages";
import { Badge } from "react-bootstrap";
import { ApplicationFlowOptionsTab } from "./Tabs/ApplicationFlowOptionsTab";
import { LanguageSelectionTab } from "./Tabs/LanguageSelectionTab";
import { Settings } from "./Tabs/SettingsTabs";
import { ClinicSiteTab } from "./Tabs/ClinicSiteTab";
import { constructSequentialOptions } from "./SettingsOptionsValues";
import { AdminOptionsLang } from "../../resources/AdminOptionsLang";
import { Loader } from "../genericComponents/Loader/Loader";
import { ITabsInfo, CustomTabContainer } from "../Tabs/Tabs";
import { MultiClinicSiteTab } from "./Tabs/MultiClinicSiteTab";
import { isNullOrUndefined } from "util";
import { IsArrayNullOrUndefinedOrEmpty } from "../../utilities/Commons";

const titleRequiredBadge = (title: string, show: boolean) => (
    <>
        {title}
        {show && <Badge style={{ color: "red", fontSize: "25px", backgroundColor: "transparent" }}>*</Badge>}
    </>
);

const tabKeys = {
    adminOptions: "adminOptions",
    clinicSite: "clinicSite",
    languages: "languages",
    settings: "settings"
}

const getFlowOptions = (props: IAdminSetupProps, textString: AdminOptionsLang) => {
    const options: FlowOption[] = [];

    if (props.featureSwitch.patientInformationModificationEnabled) {
        options.push(new FlowOption(
            textString.PersonalInformationModificationOptionTitle,
            textString.PersonalInformationModificationOptionDescription,
            props.ableToSeeAndModifyExistingPatientData,
            false,
            (value: boolean) => props.toggleAbleToViewAndModifyPatientInfo(value)
        ))
    }

    if (props.featureSwitch.regionCodeConfirmationEnabled) {
        options.push(new FlowOption(
            textString.RegionCodeConfirmationOptionTitle,
            textString.RegionCodeConfirmationOptionDescription,
            props.regionCodeConfirmationRequired,
            false,
            (value: boolean) => props.toggleRegionCodeConfirmationRequired(value)
        ));
    }

    if (props.featureSwitch.displayMyleProfilePictureEnabled) {
        options.push(new FlowOption(
            textString.DisplayProfilePictureTitle,
            textString.DisplayProfilePictureDescription,
            props.displayProfilePicture,
            false,
            (value: boolean) => props.toggleDisplayProfilePictures(value)
        ));
    }

    if (props.featureSwitch.healthCardExpiryCheckinStopEnabled) {
        options.push(new FlowOption(
            textString.HealthCardExpiryStopTitle,
            textString.HealthCardExpiryStopDescription,
            props.healthCardExpiryRedirect,
            false,
            (value: boolean) => props.toggleHealthCardExpirationRedirectToReception(value)
        ));
    }

    if (props.featureSwitch.preferredPharmacyEditionEnabled) {
        options.push(new FlowOption(
            textString.PreferredPharmacyModificationOptionTitle,
            textString.PreferredPharmacyModificationOptionDescription,
            props.ableToSeeAndModifyExistingPreferredPharmacy,
            false,
            (value: boolean) => props.toggleAbleToViewAndModifyPreferredPharmacy(value)
        ));
    }

    return options;
}

export const AdminSetupPage = (props: IAdminSetupProps) => {
    const [currentTab, setCurrentTab] = React.useState(tabKeys.adminOptions)
    useEffect(() => {
        if (!props.isUserAuthenticated) {
            props.returnToLoginPage();
        }
    }, []);
    const textResource = props.textLibrary.setAndGetCurrentLanguageResource(navigator.language.substring(0, 2) as LanguageCodes);
    const textString = {
        ...textResource.getAdminOptionString(),
        ...textResource.getGeneralStrings()
    };
    const languageLitteralStrings = textResource.getLanguageLitteralStrings();



    const settingsOptions: SettingOption[] = [
        new SettingOption(
            textString.SessionTimeSpanTitle,
            textString.SelectSessionTimeSpanInstruction,
            props.settingValues.sessionTimeSpan,
            constructSequentialOptions(1, 24, 1, (value: number) => value + "h"),
            (value: number) => props.changeAdminSettingNumberField("sessionTimeSpan", value)
        ),
        new SettingOption(
            textString.CheckInInactivityDelayTitle,
            textString.CheckInInactivityDelayDescription,
            props.settingValues.inactivityDelay,
            constructSequentialOptions(20, 90, 5, (value: number) => value + "s"),
            (value: number) => props.changeAdminSettingNumberField("inactivityDelay", value)
        ),
        new SettingOption(
            textString.LatenessDelayTitle,
            textString.LatenessDelayDescription,
            props.settingValues.latenessDelay,
            constructSequentialOptions(0, 60, 5, (value: number) => value + " min"),
            (value: number) => props.changeAdminSettingNumberField("latenessDelay", value)
        ),
        new SettingOption(
            textString.MaximumTimeGapBetweenCheckedInAppointmentsTitle,
            textString.MaximumTimeGapBetweenCheckedInAppointmentsDescription,
            props.settingValues.maximumTimeGapBetweenAppointments,
            constructSequentialOptions(5, 60, 5, (value: number) => value + " min"),
            (value: number) => props.changeAdminSettingNumberField("maximumTimeGapBetweenAppointments", value),
            props.settingValues.maximumTimeGapBetweenAppointments <= props.settingValues.checkInDelay
        ),
        new SettingOption(
            textString.CheckInDelayTitle,
            textString.CheckInDelayDescription,
            props.settingValues.checkInDelay,
            constructSequentialOptions(5, 55, 5, (value: number) => value + " min"),
            (value: number) => props.changeAdminSettingNumberField("checkInDelay", value),
            props.settingValues.maximumTimeGapBetweenAppointments <= props.settingValues.checkInDelay
        )
    ]

    const disabled = props.selectedLanguages && props.selectedLanguages.size && props.selectedLanguages.size > 3;
    const languageOptions = Object.keys(LanguageCodes).map(lang => {
        const key = LanguageCodes[lang];
        return {
            label: languageLitteralStrings.Language[key],
            checked: props.selectedLanguages.has(key),
            disabled: !props.selectedLanguages.has(key) && disabled,
            value: key,
            optionId: key
        };
    });

    const defaultLanguagesOptions = [];
    languageOptions.forEach(lang => {
        if (props.selectedLanguages.has(lang.value as LanguageCodes)) {
            defaultLanguagesOptions.push(lang);
        }
    });

    const getTabInfo = (): { [x: string]: ITabsInfo } => {
        const currentOptions: { [x: string]: ITabsInfo } = {};
        currentOptions["adminOptions"] = {
            title: titleRequiredBadge(textString.titles.KioskFlowOptionsTitle, false),
            content: <ApplicationFlowOptionsTab options={getFlowOptions(props, textResource.getAdminOptionString())} />
        };

        if (props.sites.length > 1) {
            const isMulti = props.clinicSettings.enableKioskMultiSiteCheckin;

            const titleIsRequired = isMulti
                ? !props.selectedSites || props.selectedSites.size < 1
                : !props.currentSelectedSite;

            const content = isMulti ? (
                <MultiClinicSiteTab
                    siteOptions={props.sitesOptions}
                    textString={textString}
                    onSiteSelectionChange={(checked, value) =>
                        props.onSiteSelectionChange(checked, value)
                    }
                />
            ) : (
                <ClinicSiteTab
                    currentSelectedSite={props.currentSelectedSite}
                    siteOptions={props.sitesOptions.filter(
                        site => !isNullOrUndefined(site.parentSiteId)
                    )}
                    textString={textString}
                    selectSite={props.selectCurrentSiteFromList}
                />
            );

            currentOptions["clinicSite"] = {
                title: titleRequiredBadge(textString.titles.ClinicSiteTitle, titleIsRequired),
                content
            };
        }


        currentOptions["languages"] = {
            title: titleRequiredBadge(textString.titles.LanguageTitle, !props.selectedLanguages.size),
            content: (
                <LanguageSelectionTab
                    usableLanguagesLabel={textString.labels.UsableLanguagesLabel}
                    selectDefaultLanguageLabel={textString.labels.SelectDefaultLanguage}
                    defaultLanguage={props.defaultLanguage}
                    languageOptions={languageOptions}
                    defaultLanguagesOptions={defaultLanguagesOptions}
                    onLanguageSelectionChange={(checked, languageId) => props.onLanguageSelectionChange(checked, languageId)}
                    changeDefaultLanguage={props.changeDefaultLanguage}
                />
            )
        };
        currentOptions["settings"] = {
            title: titleRequiredBadge(textString.titles.SettingsTitle, props.settingValues.maximumTimeGapBetweenAppointments <= props.settingValues.checkInDelay),
            content: (
                <Settings
                    settingsOptions={settingsOptions}
                    changeAdminSettingNumberField={props.changeAdminSettingNumberField}
                    textString={textString}
                />
            )
        };

        return currentOptions;
    };

    return (
        <div>
            <div className="row">
                <div className="col-md-12 header">
                    <h1 className="clinic-header">{textString.titles.AdminSetupTitle}</h1>
                </div>
            </div>
            <div style={{ minHeight: "650px" }}>
                <CustomTabContainer
                    currentTab={currentTab}
                    tabs={getTabInfo()}
                    setCurrentTab={(x) => setCurrentTab(x)}
                />
            </div>
            <div className="col-sm-12 text-center">
                {
                    !props.selectedLanguages.size && currentTab === tabKeys.languages &&
                    <p className="invalid">{textString.messagesInstructions.MustSelectLanguageMessage}</p>
                }
                {
                    !props.clinicSettings.enableKioskMultiSiteCheckin && !props.currentSelectedSite && currentTab === tabKeys.clinicSite &&
                    <p className="invalid">{textString.validations.SiteRequiredValidation}</p>
                }

                {
                    props.clinicSettings.enableKioskMultiSiteCheckin && IsArrayNullOrUndefinedOrEmpty(Array.from(props.selectedSites)) && currentTab === tabKeys.clinicSite &&
                    <p className="invalid" style={{marginTop:"20px"}} >{textString.validations.MultiSiteRequiredValidation}</p>
                }

                {
                    props.settingValues.maximumTimeGapBetweenAppointments <= props.settingValues.checkInDelay &&
                    currentTab === tabKeys.settings &&
                    <p className="invalid">{textString.validations.CheckInDelaySmallerThanMaximumTimeGapMessage}</p>
                }
                {
                    props.isLoadingSpinnerActive
                        ? <Loader />
                        : <button
                            className="buttons-blue"
                            onClick={e => {
                                e.preventDefault();
                                const selectedSiteName = props.sites.find(site => site.id === props.currentSelectedSite);
                                const selectedSiteNames = props.sites.filter(site => props.selectedSites.has(site.id));
                                props.startApplication({
                                    lang: props.defaultLanguage,
                                    siteName: props.clinicSettings.enableKioskMultiSiteCheckin ? selectedSiteNames.map(site => site.name).join(',') : (selectedSiteName.name || ""),
                                    siteIds: props.clinicSettings.enableKioskMultiSiteCheckin ? Array.from(props.selectedSites) : [props.currentSelectedSite],
                                    sessionTimeSpan: props.settingValues.sessionTimeSpan,
                                    latenessDelay: props.settingValues.latenessDelay,
                                    maximumTimeGapBetweenAppointments: props.settingValues.maximumTimeGapBetweenAppointments
                                },
                                    props.settingValues.inactivityDelay
                                );
                            }}
                            disabled={(props.clinicSettings.enableKioskMultiSiteCheckin ? !props.selectedSites.size : !props.currentSelectedSite) || !props.selectedLanguages.size || props.settingValues.maximumTimeGapBetweenAppointments <= props.settingValues.checkInDelay}
                        >
                            {textString.labels.StartButton}
                        </button>
                }

            </div>
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/components/AdminSetupPage/AdminSetupPage.component.tsx